import {nextTick} from 'vue'
import BaseStore from './base-store'
import StoreProxy from './store_proxy'
import {API_CHATS, API_SHARE_CHAT} from 'consts/api'
import {API2_CHATS, API2_TASKS} from 'consts/api2'
import * as errors from 'utils/errors'
import {QUESTION_VSELECT_MOCK_HISTORY} from 'components/ai-chat-v2/ai-chat-defs'
import {Later} from 'utils/timers'
import {TRACKER_MOCK_HISTORY} from 'components/widgets/tracker/tracker-mocks'
import {HEADINGS_MOCK_HISTORY} from 'components/md-content-v2/headings-mocks'


import * as $utils from 'utils';
const CHATS_LIMIT = 50

function questionsMockEnabled() {
  return new URLSearchParams(window.location.search).get('questions_mock') === '1'
}

function trackerWidgetsMocksEnabled(){
  return new URLSearchParams(window.location.search).get('tracker_mock') === '1'
}

function headingsMocksEnabled() {
  return new URLSearchParams(window.location.search).get('headings_mock') === '1'
}


export class Chat extends BaseStore {
  constructor(values = {}) {
    const default_state = {
      id: undefined,
      history: [],
      title: '',
      prompt: '',
      generating: false,
      new_messages: [],
      mode: 'deep',
      share: undefined,
      readonly: false,
      user_id: undefined,
      ref_history_len: undefined,
      task_id: undefined,
      ignore_cancelled: false,
      type: undefined,
      ...values
    }
    super(default_state, {do_not_track: ['generating', 'history', 'mode', 'share', 'ignore_cancelled', 'task_id']})
    this._default_state = $utils.object.deepCopy(default_state)
  }

  get v2() {
    return this._state.type === 'chat_v2' || this._state.type === 'onboarding_v2'
  }

  get title() {
    return this._state.title || this._state.user_query.title || this._state.user_query.text
  }

  setTitle(text) {
    this._state.title = text
  }

  _pushHistory(entry) {
    const history = this._state.history
    const last = history.at(-1)

    if (last?.intermediate) {
      // remove intermediate message if any
      this._state.history.pop()
    }

    // save new message
    this._state.history.push(entry)
    this._state.new_messages.push(entry)
  }

  _pushIntermediate(obj = {type: 'assistant'}) {
    const length = this._state.history.length
    if (length && this._state.history[length - 1].intermediate) {
      this._state.history[length - 1].data = obj.data
      return
    }

    this._state.history.push({
      intermediate: true,
      ...obj
    })
  }

  _censorInput(entry) {
    const history = this._state.history
    const last_prompt = history.findLast(x => x.type === 'user')

    Object.assign(last_prompt.data, entry)

    if (history.length <= 2 && entry.text) {
      this._state.title = entry.text
    }
  }

  async doSave(rest, changed) {
    if (changed.has('new_messages')) {
      if (this.v2) {
        this._state.task_id = null
        await rest.post(`${API2_CHATS}/${this._state.id}/history`, {'history': this._state.new_messages})
      } else {
        await rest.put(`${API_CHATS}/${this._state.id}/append`, {'history': this._state.new_messages})
      }

      this._state.new_messages = []
      changed.delete('new_messages')
    }

    if (changed.size) {
      const to_write = {}
      for (const key of changed) {
        // history can only be appended
        if (key !== 'history') {
          to_write[key] = this._state[key]
        }
      }
      const {bleach} = await rest.patch(`${API_CHATS}/${this._state.id}`, to_write)
      this._bleach(bleach)
    }
  }

  async doLoad(rest, params) {
    const mode = this._state.mode
    const chat = await rest.get(`${API_CHATS}/${this._state.id}`, params)

    // deep copy of default_state to avoid mutating objects inside it
    const default_state = $utils.object.deepCopy(this._default_state)
    Object.assign(this._state, {...default_state, ...chat, mode})

    if (this._state.type === 'chat_v2' && headingsMocksEnabled()) {
      this._state.history = $utils.object.deepCopy(HEADINGS_MOCK_HISTORY)
      return
    }

    if (this._state.type === 'chat_v2' && trackerWidgetsMocksEnabled()) {
      this._state.history = $utils.object.deepCopy(TRACKER_MOCK_HISTORY)
      return
    }

    if (this._state.type === 'chat_v2' && questionsMockEnabled()) {
      this._state.history = $utils.object.deepCopy(QUESTION_VSELECT_MOCK_HISTORY)
      return
    }

    // if there is a task - pick it up
    const task_id = this._state.task_id
    if (task_id) {
      nextTick(async() => await this._pickupTask(task_id))
      return
    }

    // if chat is empty, it means we have a new chat,
    // just start it
    const empty = this._state.history.length === 0
    if (empty) {
      nextTick(async () => await this.doChat(this._state.user_query))
      return
    }
  }

  async doChat(query, answer) {
    this._state.generating = true

    const user_query = query
    user_query.mode = this._state.mode

    if (answer) {
      if (this.v2) {
        const data = this._state.history.at(-1).data
        data.question = undefined
      }
    }

    const {bleach, task} = this.v2 ?
      await this._rest.post(`${API2_CHATS}/${this._state.id}/completions`, {user_query}) :
      await this._rest.post(`${API_CHATS}/${this._state.id}/chat`, {user_query})

    if (answer) {
      if (this.v2) {
        // here we do nothing in v2
      }
      else {
        // v1 answer behaviour - save user answer
        // and continue the same history entry
        const reasoning = this._state.history.at(-1).data.reasoning
        reasoning.at(-1).answer = query
        reasoning.push({description: 'Thanks, got it \u{2014} moving to the next step.'})
      }
    }
    else {
      this._pushHistory({type: 'user', data: bleach.user_query})
      this._pushIntermediate()
    }

    this._state.task_id = task.id
    await this._handleTask(task.id)
    this._state.generating = false
  }

  async _handleTask(task_id) {
    try {
      let censored = false
      const result = await this._tasks.waitResult(task_id, 1000, result => {
        this._pushIntermediate(result)
        if (result.censored_input && !censored) {
          this._censorInput(result.censored_input)
          censored = true
        }
      })

      if (this.v2) {
        // V2 chat always return single entry result
        const entry = result
        if (entry.data.chat_name && entry.data.chat_name !== this._state.title) {
          this.setTitle(entry.data.chat_name)
          // do not call .save, chat save on history append
          // and history should go in bulk (user + history)
        }
        if (entry.data?.question) {
        // this is ai question, do nothing, stay in an intermediate state
        // and wait for the user answer, do not save anything
          this._pushIntermediate(entry)
        } else {
        // this is a full answer, push it to history and save
          this._pushHistory(result)
          await this.save()
        }
      }
      else {
        const entry = result[0]
        if (entry.data?.reasoning?.at(-1).input) {
        // this is ai question, do nothing, stay in an intermediate state
        // and wait for the user answer, do not save anything
          this._pushIntermediate(entry)
        } else {
        // this is a full answer, push it to history and save
          result.forEach(entry => this._pushHistory(entry))
          await this.save()
        }
      }
    }
    catch (err) {
      // The 'Cancelled' error is thrown when we stop a task manually.
      // It is also thrown when we abort task listeners (e.g. in beforeUnmount).
      // We need to ignore this error only when we are just stopping the listener.
      // After a chat reload, ignore_cancelled will be false.
      if (this._state.ignore_cancelled && err instanceof errors.Cancelled) return
      this._pushHistory(Chat._map_chat_err(err))
      await this.save()
    }
  }

  async _pickupTask(task_id) {
    this._state.generating = true
    try {
      const task = await this._tasks.get(task_id)

      // If we have initial_user_query it means that
      // current task user_query is an answer to a question
      // We should always push initial_user_query to the chat history
      this._pushHistory({
        type: 'user',
        data: task.task_arg.user_query.initial_user_query || task.task_arg.user_query
      })

      this._pushIntermediate()
      await this._handleTask(task_id)
    }
    catch (err) {
      if (err.http_code !== 404) throw err

      console.log(`Task ${task_id} has expired`)
      if (this._state.history.length === 0) {
        // We've an empty chat with an expired task.
        // It means the very first user query is still valid
        // but the task was running long ago and is deleted from db.
        // Just restart initial user query
        await this.doChat(this._state.user_query)
      }
    }

    this._state.generating = false
  }

  stopChat() {
    this._tasks.cancelAll()
  }

  async shareChat(params) {
    const share = await this._rest.post(`${API_CHATS}/${this._state.id}/share`, params)
    this._state.share = share
  }

  async revokeChat() {
    await this._rest.post(`${API_CHATS}/${this._state.id}/revoke`)
    this._state.share = undefined
  }

  abortTasksListener() {
    this._state.ignore_cancelled = true
    this._rest.abortAll()
    this._tasks.cancelAll(false)
  }

  async like(idx, feedback) {
    const liked = this._state.history[idx].liked == 1 ? 0 : 1
    await this._rest.patch(`${API_CHATS}/${this._state.id}/like`, {index: idx, liked, feedback})
    this._state.history[idx].liked = liked
  }

  async dislike(idx, feedback) {
    const liked = this._state.history[idx].liked == -1 ? 0 : -1
    await this._rest.patch(`${API_CHATS}/${this._state.id}/like`, {index: idx, liked, feedback})
    this._state.history[idx].liked = liked
  }

  async savePost(index, text) {
    const {bleach} = await this._rest.patch(`${API_CHATS}/${this._state.id}/edit`, {index, text})
    this._state.history[index].data.text = bleach.text
  }

  async regenratePostFragment(history_idx, start_idx, end_idx) {
    try {
      this._state.generating = true
      const {id} = await this._rest.patch(`${API_CHATS}/${this._state.id}/regenerate_fragment`, {history_idx, start_idx, end_idx})
      const result = await this._tasks.waitResult(id)
      const text = this._state.history[history_idx].data.text

      const upd_text = text.slice(0, start_idx) + result + text.slice(end_idx)

      await this.savePost(history_idx, upd_text)
    }
    catch (err) {
      $utils.toasts.error('Failed to regenerate post section. Please try again later.')
    }
    finally {
      this._state.generating = false
    }
  }

  setMode(mode) {
    this._state.mode = mode
  }

  static _map_chat_err(err) {
    const DEFAULT_MESSAGE = 'There was an error while generating an answer. Please try again a bit later.'

    if (err instanceof errors.Cancelled) {
      return {
        data: {text: 'Answer generation cancelled by user'},
        type: 'user-cancelled'
      }
    }

    if (err instanceof errors.TaskError) {
      switch (err.task_error.code) {
        case 2:
          return {
            data: {
              text: 'Failed to scrape the source URL. Please check the URL and try again.',
              task_id: err.task_id
            },
            type: 'assistant-error'
          }
        case 1000:
          return {
            data: {
              text: 'Not enough info to generate a meaningful response.',
              task_id: err.task_id
            },
            type: 'assistant-error'
          }
        case 1001:
          return {
            data: {
              text: 'Sorry, I can\'t help with that.',
              task_id: err.task_id
            },
            type: 'assistant-error'
          }
        default:
          return {
            data: {
              text: DEFAULT_MESSAGE,
              task_id: err.task_id
            },
            type: 'error'
          }
      }
    }

    return {
      data: {
        text: DEFAULT_MESSAGE,
        // convert Error to plain dict
        // use json_short, we do not want to keep long stacks in chat history
        error: JSON.parse(errors.json_short(err))
      },
      type: 'error'
    }
  }
}

class Chats extends BaseStore {
  constructor(chat_types) {
    super({
      chats: [],
      statuses: {}
    })
    this.chat_types = chat_types
    this.task_watch = new Later(5000)
  }

  async doLoad(rest, params) {
    const chats = await rest.get(API_CHATS, {types: this.chat_types, ...params})
    this._state.chats = []
    for (const chat of chats) {
      this._state.chats.push(new Chat(chat))
    }
    await this.watchStatuses()
  }

  getChat(id) {
    return this._state.chats.find(x => x.id === id)
  }

  async removeChat(id) {
    await this._rest.delete(`${API_CHATS}/${id}`)
    this._state.chats = this._state.chats.filter(x => x.id !== id)
  }

  async _enforceChatLimit() {
    while (this._state.chats.length > CHATS_LIMIT) {
      const to_remove = this._state.chats.pop()
      await this.removeChat(to_remove.id)
    }
  }

  async newChat(user_query, type) {
    const mode = user_query.mode || 'deep'
    const api = ['chat_v2', 'onboarding_v2'].includes(type)
      ? API2_CHATS
      : API_CHATS

    const chat_data = await this._rest.post(api, {user_query, type})
    const chat = new Chat({...chat_data, mode})
    this._state.chats.unshift(chat)
    await this._enforceChatLimit()
    return chat
  }

  async duplicateChat(chat_id) {
    const duplicate = await this._rest.post(`${API_SHARE_CHAT}/${chat_id}`)
    await this._enforceChatLimit()
    return duplicate.chat_id
  }

  async watchStatuses(){
    this.task_watch.abort()
    await this._updateStatuses()
    this.task_watch.repeat(() => this._updateStatuses())
  }

  async _updateStatuses(){
    const task_ids = this._state.chats
      .filter(x => !!x.task_id)
      .map(x => x.task_id)
    if (!task_ids.length) return

    this.statuses = await this._rest.post(`${API2_TASKS}/statuses`, {task_ids})
    // Clear task IDs missing from the response so chats stop polling for unavailable tasks.
    for (const chat of this._state.chats) {
      if (task_ids.includes(chat.task_id) && !Object.hasOwn(this.statuses, chat.task_id)) {
        chat.task_id = undefined
      }
    }
  }
}

const chats = new StoreProxy(new Chats(['chat', 'chat_v2']), {array_prop: 'chats'})
export default chats

export const onboarding = new StoreProxy(new Chats('onboarding_v2'), {array_prop: 'chats'})
